import { Delete, Edit } from '@mui/icons-material';
import { Chip } from '@mui/material';
import type {
  GridColDef,
  GridRenderCellParams,
  GridRowId,
  GridRowModel,
  GridRowParams,
} from '@mui/x-data-grid-pro';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteDeviceSignalTag, useRenameDeviceSignalTag } from '~/api/device-signal-tags';
import { TableAction } from '~/components/table';
import { useAppContext } from '~/contexts';
import type { DeviceSignalTag } from '~/generated/graphql';
import { useDataGridEditing } from '~/hooks/data-grid';
import { useConfirmDialog } from '~/hooks/dialogs';
import { assert } from '~/lib/assert';

export interface Tag {
  id: GridRowId;
  name: string;
  uses: number;
}

export const useTagUtils = (signalTags: readonly DeviceSignalTag[]) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();

  const [renameTag] = useRenameDeviceSignalTag();
  const [deleteTag] = useDeleteDeviceSignalTag();

  const {
    cellModesModel,
    handleCellEditStart,
    handleCellEditStop,
    handleCellFocus,
    handleCellKeyDown,
    selectedCellParams,
    setCellModesModel,
  } = useDataGridEditing();

  const rows: Tag[] = useMemo(
    () =>
      signalTags.map(({ name, uses }) => ({
        id: name,
        name,
        uses,
      })),
    [signalTags],
  );

  const handleDelete = useCallback(
    async (tag: Tag) => {
      if (!(await confirmDelete())) return;
      await deleteTag({
        variables: {
          networkId: currentNetwork.id,
          tag: tag.name,
        },
      });
    },
    [confirmDelete, currentNetwork, deleteTag],
  );

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: 'name',
        editable: true,
        flex: 2,
        headerName: t('common.labels.name'),
        sortable: false,
      },
      {
        field: 'uses',
        editable: false,
        flex: 1,
        headerName: t('common.labels.uses'),
        sortable: false,
        renderCell: ({
          value: uses,
        }: GridRenderCellParams<DeviceSignalTag, DeviceSignalTag['uses']>) => {
          return <Chip color="primary" label={uses} />;
        },
      },
      {
        align: 'right',
        field: 'actions',
        editable: false,
        flex: 1,
        headerAlign: 'right',
        sortable: false,
        type: 'actions',
        getActions: ({ id, row: tag }: GridRowParams<(typeof rows)[number]>) => [
          <TableAction
            key={`edit-${id}`}
            title={t('pages.device_signals.list.manage_tags.actions.edit_tag')}
            Icon={Edit}
            onClick={(event) => handleCellEditStart({ id, field: 'name' }, event)}
          />,
          <TableAction
            key={`delete-${id}`}
            title={t('common.tags.delete_tag')}
            Icon={Delete}
            onClick={() => handleDelete(tag)}
          />,
        ],
      },
    ],
    [handleCellEditStart, handleDelete, t],
  );

  const processRowUpdate = useCallback(
    async (newRow: GridRowModel, oldRow: GridRowModel) => {
      const { name: oldName } = oldRow;
      const { name: newName } = newRow;
      assert(typeof oldName === 'string');
      assert(typeof newName === 'string');

      const response = await renameTag({
        variables: {
          networkId: currentNetwork.id,
          renameTo: newName,
          tag: oldName,
        },
      });

      const updatedTag = response.data?.renameTag?.network.deviceSignalTags.find(
        (x) => x.name === newName,
      );

      if (response.errors || !updatedTag) return oldRow;

      return {
        id: updatedTag.name,
        name: updatedTag.name,
        uses: updatedTag.uses,
      };
    },
    [currentNetwork, renameTag],
  );

  return {
    cellModesModel,
    columns,
    confirmDeleteProps,
    handleCellEditStart,
    handleCellEditStop,
    handleCellFocus,
    handleCellKeyDown,
    processRowUpdate,
    rows,
    selectedCellParams,
    setCellModesModel,
  };
};
